import {
  PREPR_PAGE_TYPES,
  SEARCH_RESULTS_PER_PAGE,
} from '@constants/constants';
import useLocale from '@hooks/useLocale';
import useTranslations from '@hooks/useTranslations';
import Card from '@molecules/Card/Card';
import CheckboxInput from '@molecules/CheckboxInput/CheckboxInput';
import ExploreCard from '@molecules/ExploreCard/ExploreCard';
import AlgoliaFilters from '@molecules/Filters/AlgoliaFilters';
import AlgoliaPagination from '@molecules/Pagination/AlgoliaPagination';
import SectionContent from '@molecules/Section/SectionContent';
import { OverviewItem } from '@type-declarations/overviewItems';
import getInternalRoute from '@utils/getInternalRoute';
import {
  AlgoliaType,
  mapAlgoliaTypeToPreprType,
} from '@utils/mapAlgoliaTypeToPreprType';
import clsx from 'clsx';
import { startOfDay } from 'date-fns';
import { useMemo } from 'react';
import { useHits } from 'react-instantsearch';

import NoResults from './NoResults';
import NoResultsBoundary from './NoResultsBoundary';
import styles from './SearchResults.module.scss';

interface AlgoliaHit {
  id: string;
  distinctID: string;
  model: AlgoliaType;
  slug: string;
  image?: Array<{ url: string }>;
  isMoreToExplore?: boolean;
}

interface Props {
  recommendations: OverviewItem[];
  recommendationsTitle: string;
  recommendationsText?: string;
  noResultItems: OverviewItem[];
  noResultsTitle: string;
  noResultsText?: string;
  moreToExploreTitle: string;
  moreToExploreSubtitle: string;
  moreToExploreCta: string;
  moreToExploreLayOver: string;
  moreToExplore?: OverviewItem[];
  showPastEvents: boolean;
  setShowPastEvents: (showPastEvents: boolean) => void;
}

export default function SearchResults({
  recommendations,
  recommendationsTitle,
  recommendationsText,
  noResultItems,
  noResultsTitle,
  noResultsText,
  moreToExploreTitle,
  moreToExploreSubtitle,
  moreToExploreCta,
  moreToExploreLayOver,
  moreToExplore,
  showPastEvents,
  setShowPastEvents,
}: Props) {
  const t = useTranslations();
  const { results } = useHits();

  const locale = useLocale();

  let title = `${results?.nbHits} ${(results?.nbHits === 1
    ? t.result
    : t.results
  ).toLowerCase()}`;
  let text = '';

  if (!results?.query && recommendations?.length) {
    title = recommendationsTitle;
    text = recommendationsText || '';
  } else if (results?.query && !results?.nbHits) {
    title = noResultsTitle;
    text = noResultsText || '';
  }

  // Use useMemo to calculate the enhanced hits once to avoid layout shifts
  const enhancedHits = useMemo(() => {
    if (!results?.hits || !moreToExplore?.length) return results?.hits || [];

    if (results.hits.length <= 8) {
      return results.hits;
    }

    const hitsCopy = [...results.hits];
    const randomMoreToExploreItem =
      moreToExplore[Math.floor(Math.random() * moreToExplore.length)];

    let randomIndex = Math.max(
      5,
      Math.min(
        SEARCH_RESULTS_PER_PAGE,
        Math.floor(
          Math.random() * Math.min(hitsCopy.length, SEARCH_RESULTS_PER_PAGE),
        ),
      ),
    );

    if (randomIndex % 2 !== 0) {
      randomIndex =
        randomIndex === SEARCH_RESULTS_PER_PAGE
          ? randomIndex - 1
          : randomIndex + 1;
    }

    // Create a fake Algolia hit with required properties
    const fakeAlgoliaHit = {
      ...randomMoreToExploreItem,
      isMoreToExplore: true,
      __position: -1,
      objectID: randomMoreToExploreItem.id || 'more-to-explore',
      distinctID: randomMoreToExploreItem.id || 'more-to-explore',
      model: 'LocationPage', // fallback type
      slug: randomMoreToExploreItem.slug || '',
      // Add any other required properties if needed
    };

    hitsCopy.splice(randomIndex, 0, fakeAlgoliaHit);

    return hitsCopy;
  }, [results?.hits, moreToExplore]);

  const getCardHref = (item: OverviewItem | AlgoliaHit) => {
    if ('isMoreToExplore' in item && item.isMoreToExplore) {
      return getInternalRoute({ internalLink: item as OverviewItem, locale });
    }

    const algoliaItem = item as AlgoliaHit;
    const type = mapAlgoliaTypeToPreprType(algoliaItem.model);
    if (['SubCategory', 'SubSubCategory'].includes(type)) {
      return `/redirect/${type}/${algoliaItem.slug}`;
    }

    return getInternalRoute({
      algolia: true,
      internalLink: {
        slug: algoliaItem.slug,
        typename: type,
      },
      locale,
    });
  };

  // Helper to map a raw Algolia hit to AlgoliaHit type
  function toAlgoliaHit(item: unknown): AlgoliaHit {
    if (
      typeof item === 'object' &&
      item !== null &&
      'id' in item &&
      'distinctID' in item &&
      'model' in item &&
      'slug' in item
    ) {
      const typed = item as {
        id: string;
        distinctID: string;
        model: AlgoliaType;
        slug: string;
        image?: Array<{ url: string }>;
        isMoreToExplore?: boolean;
      };
      return {
        id: typed.id,
        distinctID: typed.distinctID,
        model: typed.model,
        slug: typed.slug,
        image: typed.image,
        isMoreToExplore: typed.isMoreToExplore,
      };
    }
    throw new Error('Invalid Algolia hit');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const checkIfEventIsPast = (item: any) => {
    if (item.model !== 'Pagina: Evenement') return undefined;

    const startOfToday = Math.floor(startOfDay(new Date()).getTime() / 1000);

    if (item.final_date && item.final_date < startOfToday) {
      return t.eventPast;
    }

    if (
      !item.final_date &&
      item.date &&
      item.date[1] &&
      item.date[1] < startOfToday
    ) {
      return t.eventPast;
    }

    // Fallback to event page type
    return t.card[PREPR_PAGE_TYPES.EVENT_PAGE];
  };

  return (
    <div className={clsx(styles.container, 'u-mb-section')}>
      <SectionContent
        className="u-container-width u-mt-8"
        title={title}
        text={text}
      />

      {/* Show content selection when no query is available */}
      {!results?.query && (
        <div
          className={clsx(
            'u-container-width u-grid u-grid--gap-3 u-grid--gap-4@lg u-grid--2 u-grid--3@sm u-grid--4@lg',
          )}
        >
          {recommendations.map(item => (
            <Card
              {...item}
              key={item.id}
              id={item.id}
              showType
              showSaveButton
              imageSize="sm"
              imageSizeBreakpoints={['md@from-md', 'lg@from-lg']}
              href={getInternalRoute({ internalLink: item, locale })}
            />
          ))}
        </div>
      )}

      {/* Algolia results or as fallback the no results selection */}
      {!!results?.query && (
        <NoResultsBoundary fallback={<NoResults items={noResultItems} />}>
          <div className={clsx(styles.topBar, 'u-container-width')}>
            <AlgoliaFilters />
            <CheckboxInput
              label={t.showPastEvents}
              id="show-past-events"
              name="show-past-events"
              value="show-past-events"
              checked={showPastEvents}
              noBorder
              noShadow
              className={styles.checkbox}
              onChange={() => setShowPastEvents(!showPastEvents)}
            />
          </div>
          <div
            className={clsx(
              'u-grid u-grid--gap-3 u-grid--gap-4@lg u-grid--2 u-grid--3@sm u-grid--4@lg u-container-width',
            )}
          >
            {enhancedHits.map(item =>
              item.isMoreToExplore ? (
                <ExploreCard
                  key={item.id}
                  href={getInternalRoute({
                    internalLink: item as unknown as OverviewItem,
                    locale,
                  })}
                  moreToExploreTitle={moreToExploreTitle}
                  moreToExploreSubtitle={moreToExploreSubtitle}
                  moreToExploreCta={moreToExploreCta}
                  moreToExploreLayOver={moreToExploreLayOver}
                />
              ) : (
                <Card
                  {...item}
                  key={item.distinctID}
                  id={item.id}
                  showType
                  typename={(() => {
                    const mapped = mapAlgoliaTypeToPreprType(item.model);
                    const preprPages: string[] = [
                      PREPR_PAGE_TYPES.NEWS_OVERVIEW_PAGE,
                      PREPR_PAGE_TYPES.NEWS_PAGE,
                      PREPR_PAGE_TYPES.EVENT_OVERVIEW_PAGE,
                      PREPR_PAGE_TYPES.EVENT_PAGE,
                      PREPR_PAGE_TYPES.ACTIVITY_OVERVIEW_PAGE,
                      PREPR_PAGE_TYPES.ACTIVITY_PAGE,
                      PREPR_PAGE_TYPES.LOCATION_OVERVIEW_PAGE,
                      PREPR_PAGE_TYPES.LOCATION_PAGE,
                      PREPR_PAGE_TYPES.ROUTE_OVERVIEW_PAGE,
                      PREPR_PAGE_TYPES.ROUTE_PAGE,
                      PREPR_PAGE_TYPES.HOME_PAGE,
                      PREPR_PAGE_TYPES.EXPLORE_PAGE,
                      PREPR_PAGE_TYPES.CONTENT_PAGE,
                      PREPR_PAGE_TYPES.MAP_PAGE,
                      PREPR_PAGE_TYPES.SHARED_LIST_PAGE,
                      PREPR_PAGE_TYPES.ZIGZAG_CAMPAIGN_PAGE,
                      PREPR_PAGE_TYPES.SEARCH_PAGE,
                      PREPR_PAGE_TYPES.LANDING_PAGE,
                    ];
                    if (preprPages.includes(mapped)) {
                      return mapped as import('@type-declarations/preprPage').PreprPage;
                    }
                    return undefined;
                  })()}
                  showSaveButton
                  image={
                    item.image?.[0]
                      ? {
                          ...item.image[0],
                          id: item.id,
                          placeholder: '', // fallback or real placeholder
                          width: 400, // fallback width
                          height: 300, // fallback height
                        }
                      : undefined
                  }
                  noBlur
                  imageSize="sm"
                  imageSizeBreakpoints={['md@from-md', 'lg@from-lg']}
                  href={getCardHref(toAlgoliaHit(item))}
                  title={item.title}
                  subtitle={checkIfEventIsPast(item)}
                />
              ),
            )}
          </div>

          <AlgoliaPagination className="u-mt-8" />
        </NoResultsBoundary>
      )}
    </div>
  );
}
